import { createListenerMiddleware } from '@reduxjs/toolkit';
import { accountsApi } from './accountsApi';
import { diversificationsApi } from './diversificationsApi';
import { documentsApi } from './documentsApi';
import { holdingsApi } from './holdingsApi';
import type { WARootState } from './store';
import { transactionsApi } from './transactionsApi';
import { userDataApis } from './userDataApis';
import { usersApi } from './usersApi';
import { setSelectedViewingTag } from './viewingTagSlice';

/**
 * Middleware that listens for user/role changes and intelligently invalidates user-specific caches
 */
export const cacheInvalidationMiddleware = createListenerMiddleware();
const startCacheInvalidationListening =
  cacheInvalidationMiddleware.startListening.withTypes<WARootState>();

type CacheListenerApi = Parameters<
  Parameters<typeof startCacheInvalidationListening>[0]['effect']
>[1];

function invalidateAccountTagCatalog(
  listenerApi: CacheListenerApi,
  wealthAccountIds: number[],
): void {
  listenerApi.dispatch(
    accountsApi.util.invalidateTags([
      { type: 'AccountTags', id: 'LIST' },
      ...wealthAccountIds.map((id) => ({ type: 'AccountTags' as const, id })),
    ]),
  );
}

startCacheInvalidationListening({
  actionCreator: setSelectedViewingTag,
  effect: (_action, listenerApi) => {
    listenerApi.dispatch(
      accountsApi.util.invalidateTags([
        { type: 'Accounts', id: 'LIST' },
        'AccountsHistorical',
      ]),
    );
    listenerApi.dispatch(
      holdingsApi.util.invalidateTags([{ type: 'Holdings', id: 'LIST' }]),
    );
  },
});

startCacheInvalidationListening({
  matcher: usersApi.endpoints.createUserTag.matchFulfilled,
  effect: (action, listenerApi) => {
    invalidateAccountTagCatalog(
      listenerApi,
      action.meta.arg.originalArgs.wealthAccountIds,
    );
  },
});

function invalidateTagFilteredSurfaces(listenerApi: CacheListenerApi): void {
  listenerApi.dispatch(
    accountsApi.util.invalidateTags([
      'Accounts',
      'Portfolio',
      'AccountsHistorical',
    ]),
  );
  listenerApi.dispatch(
    diversificationsApi.util.invalidateTags([
      'HistoricalDiversification',
      'Diversification',
    ]),
  );
  listenerApi.dispatch(
    documentsApi.util.invalidateTags([
      { type: 'Documents', id: 'LIST' },
      { type: 'Documents2', id: 'LIST' },
    ]),
  );
  listenerApi.dispatch(
    holdingsApi.util.invalidateTags([{ type: 'Holdings', id: 'LIST' }]),
  );
  listenerApi.dispatch(
    transactionsApi.util.invalidateTags([
      { type: 'BrokerageTransactions', id: 'LIST' },
      'BankTransactionsOverview',
      'WizardEligibility',
      'BankTransactionsChart',
      'BankTransactions',
    ]),
  );
}

function invalidateUserTagsAfterAccountSave(
  listenerApi: CacheListenerApi,
  arg: { TagIds?: number[] },
): void {
  if (!Array.isArray(arg.TagIds)) {
    return;
  }
  // Account saves write membership through TagIds. Drop cached tag-account
  // lists so the tag dialog does not PUT a stale set back, and refresh every
  // surface filtered by that membership.
  listenerApi.dispatch(usersApi.util.invalidateTags([{ type: 'UserTags' }]));
  invalidateTagFilteredSurfaces(listenerApi);
}

startCacheInvalidationListening({
  matcher: accountsApi.endpoints.createOfflineAccount.matchFulfilled,
  effect: (action, listenerApi) => {
    invalidateUserTagsAfterAccountSave(
      listenerApi,
      action.meta.arg.originalArgs,
    );
  },
});

startCacheInvalidationListening({
  matcher: accountsApi.endpoints.updateOfflineAccount.matchFulfilled,
  effect: (action, listenerApi) => {
    invalidateUserTagsAfterAccountSave(
      listenerApi,
      action.meta.arg.originalArgs,
    );
  },
});

startCacheInvalidationListening({
  matcher: accountsApi.endpoints.updateAccountTags.matchFulfilled,
  effect: (action, listenerApi) => {
    invalidateUserTagsAfterAccountSave(
      listenerApi,
      action.meta.arg.originalArgs,
    );
  },
});

startCacheInvalidationListening({
  matcher: usersApi.endpoints.updateUserTag.matchFulfilled,
  effect: (action, listenerApi) => {
    invalidateAccountTagCatalog(
      listenerApi,
      action.meta.arg.originalArgs.wealthAccountIds,
    );
    invalidateTagFilteredSurfaces(listenerApi);
  },
});

// Track the last known user identity to detect actual user changes
// let lastUserIdentity: string | null = null;

startCacheInvalidationListening({
  predicate: (_action, currentState, previousState) => {
    const currentAuth = currentState.auth;
    const previousAuth = previousState.auth;

    // Create user identity from user + role combination
    const currentIdentity =
      currentAuth.user && currentAuth.currentRole
        ? `${currentAuth.user}:${currentAuth.currentRole}`
        : null;

    const previousIdentity =
      previousAuth.user && previousAuth.currentRole
        ? `${previousAuth.user}:${previousAuth.currentRole}`
        : null;

    const userIdentityChanged =
      previousIdentity !== null &&
      currentIdentity !== null &&
      previousIdentity !== currentIdentity;

    // Reset lastUserIdentity on logout so re-login always triggers invalidation
    const loggingOut = previousIdentity !== null && currentIdentity === null;
    if (loggingOut) {
      // lastUserIdentity = null;
    }

    // Invalidate when logging in after logout (any user, including same user)
    const loggingInAfterLogout =
      previousIdentity === null && currentIdentity !== null;

    // Update last known identity for next comparison
    if (currentIdentity) {
      // lastUserIdentity = currentIdentity;
    }

    return userIdentityChanged || loggingInAfterLogout;
  },
  effect: (_action, listenerApi) => {
    console.log('User identity changed, invalidating user-specific caches');

    // Reset all user-specific API caches when user identity changes so no
    // previous user's data is served to the new session.
    userDataApis.forEach((api) =>
      listenerApi.dispatch(api.util.resetApiState()),
    );
  },
});
